import { useEffect, useMemo, useState } from 'react';
import ZoomableMap from './ZoomableMap';
import { geoIdentity, geoPath } from 'd3-geo';
import axios from 'axios';
import zhConvertor from 'zhconvertor';
import nsGeo from './negerisembilan/negerisembilan_dun.json';
import './negerisembilan/negerisembilan.css';

// Alliance colours come from getInfo (canonical alliances.color_hex) per seat.

// Hardcoded district names. Chinese is stored traditional (繁體) and converted
// to simplified by tr() unless the site language is zh_TW — matching the rest of
// SeatDetail (e.g. SeatInfoPanel.jsx). English is the romanised Malay name.
const SEAT_NAMES = {
  N1: { c: '真納', e: 'Chennah' },
  N2: { c: '葫蘆頂', e: 'Pertang' },
  N3: { c: '雙溪鐳', e: 'Sungai Lui' },
  N4: { c: '格拉望', e: 'Klawang' },
  N5: { c: '斯汀', e: 'Serting' },
  N6: { c: '巴弄', e: 'Palong' },
  N7: { c: '遮蘭巴當', e: 'Jeram Padang' },
  N8: { c: '馬口', e: 'Bahau' },
  N9: { c: '冷京', e: 'Lenggeng' },
  N10: { c: '汝來', e: 'Nilai' },
  N11: { c: '羅白', e: 'Lobak' },
  N12: { c: '沉香', e: 'Temiang' },
  N13: { c: '小甘密', e: 'Sikamat' },
  N14: { c: '安邦岸', e: 'Ampangan' },
  N15: { c: '爪西', e: 'Juasseh' },
  N16: { c: '神安池', e: 'Seri Menanti' },
  N17: { c: '新那靈', e: 'Senaling' },
  N18: { c: '庇勞', e: 'Pilah' },
  N19: { c: '柔河', e: 'Johol' },
  N20: { c: '拉務', e: 'Labu' },
  N21: { c: '武吉甲巴央', e: 'Bukit Kepayang' },
  N22: { c: '拉杭', e: 'Rahang' },
  N23: { c: '萬茂', e: 'Mambau' },
  N24: { c: '龍城', e: 'Seremban Jaya' },
  N25: { c: '芭蕾', e: 'Paroi' },
  N26: { c: '真蓬', e: 'Chembong' },
  N27: { c: '晏斗', e: 'Rantau' },
  N28: { c: '高島', e: 'Kota' },
  N29: { c: '朱湖', e: 'Chuah' },
  N30: { c: '蘆骨', e: 'Lukut' },
  N31: { c: '峇眼檳榔', e: 'Bagan Pinang' },
  N32: { c: '寧宜', e: 'Linggi' },
  N33: { c: '斯里丹絨', e: 'Sri Tanjung' },
  N34: { c: '金馬士', e: 'Gemas' },
  N35: { c: '利民濟', e: 'Gemencheh' },
  N36: { c: '勒巴', e: 'Repah' },
};

const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

const cnName = (seat) => (SEAT_NAMES[seat] ? tr(SEAT_NAMES[seat].c) : seat);
const enName = (seat) => (SEAT_NAMES[seat] ? SEAT_NAMES[seat].e : '');

// Default fill for a district with no result yet.
const DEFAULT_FILL = '#e2e6ee';
// Seam between districts.
const SEAM = '#ffffff';

// 2.5D layout. Negeri Sembilan is nearly square (~1.11 aspect); leave DEPTH px
// at the bottom of the viewBox for the extruded side walls + a little shadow.
const W = 820;
const H = 760;
const PAD = 26;
const DEPTH = 22;        // extrusion thickness (uniform)
const WALL_DX = 3;       // light comes from upper-left -> walls drop down-right

// Darken a #rrggbb colour towards black by `amount` (0..1) for the side walls.
const shade = (hex, amount = 0.4) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#9aa0ad';
  const n = parseInt(m[1], 16);
  const r = Math.round(((n >> 16) & 255) * (1 - amount));
  const g = Math.round(((n >> 8) & 255) * (1 - amount));
  const b = Math.round((n & 255) * (1 - amount));
  return `rgb(${r}, ${g}, ${b})`;
};

const Sembilan = (props) => {
  const { state, code, lastElection, static: isStatic, onSeatClick, fills } = props;

  // Highlight shared between the map and the name grid (purely local — locating
  // a district from its list row and vice-versa).
  const [activeSeat, setActiveSeat] = useState(null);

  // Project the GeoJSON once -> a top-face path string + label centroid per
  // district.
  const districts = useMemo(() => {
    // Planar projection (lon/lat treated as x/y, Y flipped for SVG), matching
    // MalaysiaMap.jsx. Avoids d3-geo's spherical winding rule, which would
    // otherwise render each district as the giant complement of its true shape.
    const projection = geoIdentity().reflectY(true).fitExtent(
      [[PAD, PAD], [W - PAD, H - PAD - DEPTH]],
      nsGeo,
    );
    const path = geoPath(projection);
    return nsGeo.features
      .map((f) => {
        const [cx, cy] = path.centroid(f);
        return { seat: f.properties.seat, d: path(f), cx, cy };
      })
      .filter((dist) => dist.d);
  }, []);

  // Poll the results API (same contract as before) — but only when the parent
  // didn't supply `fills` (e.g. SeatsDetail derives colours from its own
  // seatinfo poll, so this component must not issue a duplicate getInfo poll).
  const [maps, setMaps] = useState([]);
  const hasFills = fills != null;
  useEffect(() => {
    if (hasFills) return undefined;
    let active = true;
    const callMap = () => {
      const url = `public-api/getInfo?state=${state}&code=${code}${lastElection ? '&last_election=true' : ''}`;
      axios
        .get(url)
        .then((res) => { if (active) setMaps(Array.isArray(res.data) ? res.data : []); })
        .catch((err) => console.log(err));
    };
    callMap();
    const interval = setInterval(callMap, 15000);
    return () => {
      active = false;
      clearInterval(interval);
      setMaps([]);
    };
  }, [state, code, lastElection, hasFills]);

  // seat -> winning alliance colour. Empty maps (e.g. current election with no
  // results yet) naturally falls back to DEFAULT_FILL, so colours never get
  // stuck when toggling 上届/本届.
  const fillBySeat = useMemo(() => {
    if (fills != null) return fills;
    const out = {};
    for (let i = 0; i < maps.length; i += 1) {
      const c = maps[i].color;
      if (c) out[maps[i].seat] = c;
    }
    return out;
  }, [maps, fills]);

  const handleClick = (seat) => {
    if (onSeatClick) onSeatClick(seat);
  };
  const labelOf = (seat) => `${seat} ${cnName(seat)}${enName(seat) ? ` · ${enName(seat)}` : ''}`;

  const svg = (
    <svg
      id="state-map"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <filter id="ns-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="7" stdDeviation="7" floodColor="#1c2330" floodOpacity="0.28" />
        </filter>
      </defs>

      {/* Extruded side walls: each district's dark twin, offset down-right and
          drawn behind the top faces. Adjacent tiles hide interior walls, so the
          state reads as one solid raised slab. */}
      <g transform={`translate(${WALL_DX}, ${DEPTH})`} filter="url(#ns-shadow)">
        {districts.map((dist) => (
          <path
            key={`wall-${dist.seat}`}
            d={dist.d}
            fill={shade(fillBySeat[dist.seat] || DEFAULT_FILL, 0.45)}
          />
        ))}
      </g>

      {/* Top faces: colour, seam stroke, interactivity. id={seat} preserves the
          existing `.sabah_map_clickable path[id^="N"]:hover` brightness effect. */}
      <g>
        {districts.map((dist) => {
          const isActive = activeSeat === dist.seat;
          return (
            <path
              key={dist.seat}
              id={dist.seat}
              className={`ns-tile ${isActive ? 'is-active' : ''}`}
              d={dist.d}
              fill={fillBySeat[dist.seat] || DEFAULT_FILL}
              stroke={isActive ? '#11161f' : SEAM}
              strokeWidth={isActive ? 2.6 : 1.4}
              strokeLinejoin="round"
              tabIndex={onSeatClick ? 0 : undefined}
              onClick={onSeatClick ? () => handleClick(dist.seat) : undefined}
              onMouseEnter={() => setActiveSeat(dist.seat)}
              onMouseLeave={() => setActiveSeat((s) => (s === dist.seat ? null : s))}
              onFocus={() => setActiveSeat(dist.seat)}
              onBlur={() => setActiveSeat((s) => (s === dist.seat ? null : s))}
              onKeyDown={onSeatClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(dist.seat); }
              } : undefined}
              aria-label={labelOf(dist.seat)}
            >
              <title>{labelOf(dist.seat)}</title>
            </path>
          );
        })}
      </g>

      {/* Inline seat-number labels at each centroid (pointer-events:none so taps
          fall through to the district). Skipped on the tiny static render. */}
      {!isStatic && (
        <g className="ns-labels">
          {districts.map((dist) => (
            <text
              key={`lbl-${dist.seat}`}
              x={dist.cx}
              y={dist.cy}
              className="ns-label"
              textAnchor="middle"
              dominantBaseline="central"
            >
              {dist.seat}
            </text>
          ))}
        </g>
      )}
    </svg>
  );

  if (isStatic) {
    return <div className="mapinner">{svg}</div>;
  }

  return (
    <ZoomableMap contentClassName="mapinner sabah_map_clickable">{svg}</ZoomableMap>
  );
};

export default Sembilan;
